import { useMetaData } from '../queries/useMetaData.js'
import { useAllDms } from '../queries/useDms.js'
import { useUIStore } from '../store/uiStore.js'
import { PILLARS } from '../data/pillars.js'
import { timeAgo } from '../data/metaKeys.js'
import { colorFor } from '../data/userColors.js'
import NewGroupDmButton from './NewGroupDmButton.jsx'

/**
 * PresenceBar — shows who's active on the current pillar + briefly who's
 * elsewhere.
 *
 * Reads meta.presence (a map of { userName: { at, pillar } }) and
 * groups into:
 *   - ON THIS PAGE: users whose pillar === activePillar
 *   - RECENTLY ACTIVE: users on other pillars, heartbeat within the last hour
 *
 * An "online now" dot is shown for heartbeats within the last 60 seconds.
 * Otherwise we show "3m ago", "1h ago", etc.
 *
 * This updates whenever meta refetches (every 60s staleTime or on window
 * focus). Real-time updates come in the Ably session.
 */
export default function PresenceBar() {
  const { data: meta } = useMetaData()
  const activePillarIndex = useUIStore((s) => s.activePillar)
  const activePillar = PILLARS[activePillarIndex]?.name
  const currentUser = useUIStore((s) => s.currentUser)

  const presence = meta.presence || {}
  const now = Date.now()
  const ONLINE_THRESHOLD = 60_000 // 60s
  const RECENT_THRESHOLD = 60 * 60_000 // 1 hour

  // Turn the presence map into an array, filter out ancient entries
  const entries = Object.entries(presence)
    .map(([name, info]) => ({
      name,
      at: info?.at || 0,
      pillar: info?.pillar || 'Unknown',
      onlineNow: info?.at && now - info.at < ONLINE_THRESHOLD,
      isMe: name === currentUser,
    }))
    .filter((e) => e.at > 0 && now - e.at < RECENT_THRESHOLD)
    .sort((a, b) => {
      // Me first, then online-now, then most-recent
      if (a.isMe !== b.isMe) return a.isMe ? -1 : 1
      if (a.onlineNow !== b.onlineNow) return a.onlineNow ? -1 : 1
      return b.at - a.at
    })

  if (entries.length === 0) return null

  const onThisPage = entries.filter((e) => e.pillar === activePillar)
  const elsewhere = entries.filter((e) => e.pillar !== activePillar)

  return (
    <div
      data-tour="presence-bar"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        marginBottom: 16,
        padding: '10px 12px',
        background: '#fff',
        border: '1px solid var(--border)',
        borderRadius: 10,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.08,
          marginRight: 4,
        }}
      >
        On this page
      </div>
      {onThisPage.length > 0 ? (
        onThisPage.map((e) => <PresencePill key={e.name} entry={e} current />)
      ) : (
        <span style={{ fontSize: 11, color: 'var(--muted-light)', fontStyle: 'italic' }}>
          just you
        </span>
      )}

      {elsewhere.length > 0 && (
        <>
          <div
            style={{
              borderLeft: '1px solid var(--border)',
              height: 20,
              margin: '0 4px',
            }}
          />
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: 'var(--muted)',
              textTransform: 'uppercase',
              letterSpacing: 0.08,
              marginRight: 4,
            }}
          >
            Elsewhere
          </div>
          {elsewhere.map((e) => (
            <PresencePill key={e.name} entry={e} current={false} />
          ))}
        </>
      )}

      {/* Always-available entry point to start a DM or group chat */}
      <div style={{ marginLeft: 'auto' }}>
        <NewGroupDmButton />
      </div>
    </div>
  )
}

function PresencePill({ entry, current }) {
  const { name, onlineNow, pillar, at, isMe } = entry
  const color = colorFor(name)
  const pillarInfo = PILLARS.find((p) => p.name === pillar)
  const openDmWith = useUIStore((s) => s.openDmWith)
  const { threads } = useAllDms()
  const thread = threads.find((t) => t.other.length === 1 && t.other[0] === name)
  const unread = thread?.unread || 0

  const handleClick = () => {
    if (isMe) return
    openDmWith(name)
  }

  return (
    <span
      onClick={handleClick}
      title={
        isMe
          ? `${name} (you)`
          : onlineNow
            ? `${name} is on ${pillar} now — click to message`
            : `${name} · last seen ${timeAgo(at)} on ${pillar} — click to message`
      }
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: isMe ? '#eff6ff' : '#f9fafb',
        border: `1px solid ${isMe ? '#bfdbfe' : 'var(--border)'}`,
        borderRadius: 99,
        padding: '3px 10px 3px 4px',
        fontSize: 11,
        fontWeight: 600,
        color: 'var(--text-secondary)',
        opacity: current ? 1 : 0.8,
        cursor: isMe ? 'default' : 'pointer',
        position: 'relative',
        transition: 'background 0.15s',
      }}
      onMouseEnter={(e) => {
        if (!isMe) e.currentTarget.style.background = '#f3f4f6'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = isMe ? '#eff6ff' : '#f9fafb'
      }}
    >
      {unread > 0 && !isMe && (
        <span
          style={{
            position: 'absolute',
            top: -4,
            right: -4,
            background: '#ef4444',
            color: '#fff',
            borderRadius: 99,
            fontSize: 9,
            fontWeight: 800,
            padding: '1px 5px',
            minWidth: 14,
            textAlign: 'center',
            border: '1.5px solid #fff',
          }}
        >
          {unread > 9 ? '9+' : unread}
        </span>
      )}
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: color,
          color: '#fff',
          fontSize: 10,
          fontWeight: 800,
          lineHeight: '20px',
          textAlign: 'center',
          position: 'relative',
        }}
      >
        {name[0]}
        {onlineNow && (
          <span
            style={{
              position: 'absolute',
              right: -2,
              bottom: -2,
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#22c55e',
              border: '2px solid #fff',
            }}
          />
        )}
      </span>
      <span>{name}</span>
      {!current && pillarInfo && (
        <span style={{ color: 'var(--muted-light)', fontSize: 10, fontWeight: 500 }}>
          · {pillarInfo.icon}
        </span>
      )}
      {!onlineNow && (
        <span style={{ color: 'var(--muted-light)', fontSize: 9, fontWeight: 500 }}>
          {timeAgo(at)}
        </span>
      )}
    </span>
  )
}

